import { z } from 'zod';

/**
 * Wire schemas for the admin xlsx import endpoints.
 *
 * The backend `ImportPlannerService` produces a per-row `ImportPlan` describing
 * what would change against a snapshot of the DB. The same shape is returned
 * for both dry-run (no writes) and apply (writes committed) requests. When
 * applying, the response also contains `applied: true` so the UI knows the
 * plan was committed, not just previewed.
 */

export const importRowActionEnum = z.enum(['create', 'update', 'unchanged', 'error']);
export type ImportRowAction = z.infer<typeof importRowActionEnum>;

export const importPlanRowReportSchema = z.object({
  sheet: z.string(),
  rowNumber: z.number().int().nonnegative(),
  action: importRowActionEnum,
  identifier: z.string(),
  changedKeys: z.array(z.string()).optional(),
  message: z.string().optional(),
  // The parsed-row payload is intentionally `unknown` on the wire — the UI only
  // needs to display the row outcome, not the per-entity field shape.
  data: z.unknown().optional(),
});
export type ImportPlanRowReport = z.infer<typeof importPlanRowReportSchema>;

export const importPlanTotalsSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
});
export type ImportPlanTotals = z.infer<typeof importPlanTotalsSchema>;

export const importPlanReportSchema = z.object({
  totals: importPlanTotalsSchema,
  rows: z.array(importPlanRowReportSchema),
});
export type ImportPlanReport = z.infer<typeof importPlanReportSchema>;

export const importMaterialsResponseSchema = z.object({
  plan: importPlanReportSchema,
  applied: z.boolean(),
});
export type ImportMaterialsResponse = z.infer<typeof importMaterialsResponseSchema>;

export const importEdgesResponseSchema = z.object({
  plan: importPlanReportSchema,
  applied: z.boolean(),
});
export type ImportEdgesResponse = z.infer<typeof importEdgesResponseSchema>;

/**
 * Drawer-systems imports return two parallel reports — one for the parent
 * `DrawerSystem` rows and one for their `DrawerSystemVariant` children.
 * Variants are matched by `(systemId, description)` (or SKU when set), so the
 * UI may show "system created/unchanged" alongside per-variant outcomes.
 */
export const importDrawerSystemsPlanSchema = z.object({
  systems: importPlanReportSchema,
  variants: importPlanReportSchema,
});
export type ImportDrawerSystemsPlan = z.infer<typeof importDrawerSystemsPlanSchema>;

export const importDrawerSystemsResponseSchema = z.object({
  plan: importDrawerSystemsPlanSchema,
  applied: z.boolean(),
});
export type ImportDrawerSystemsResponse = z.infer<typeof importDrawerSystemsResponseSchema>;

export const importHardwareResponseSchema = z.object({
  plan: importPlanReportSchema,
  applied: z.boolean(),
});
export type ImportHardwareResponse = z.infer<typeof importHardwareResponseSchema>;

export const importLightingResponseSchema = z.object({
  plan: importPlanReportSchema,
  applied: z.boolean(),
});
export type ImportLightingResponse = z.infer<typeof importLightingResponseSchema>;
